import { useEffect } from 'react'
import { useVoiceCallSession } from '../../../hooks/use-voice-call-session'
import { noteIncomingScreenShown } from '../utils/incoming-ring-timing'

/**
 * Records `ctrl:incoming:screenShown` (surface `in-app-overlay`) once the
 * in-app incoming-call overlay is actually on screen: the session is ringing,
 * no OS ring surface (CallKit) stands in for the overlay, and the page is
 * visible — a hidden WebView (backgrounded Android) only counts once it is
 * foregrounded. Measured on the next frame so it reflects the paint.
 */
export function useIncomingScreenTelemetry(): void {
  const session = useVoiceCallSession()
  const callId = session?.callId ?? null
  const ringingInApp = session?.phase === 'incoming' && session.osRingActive !== true

  useEffect(() => {
    if (!callId || !ringingInApp || typeof document === 'undefined') return
    let frame: number | null = null
    const markWhenVisible = () => {
      if (document.visibilityState !== 'visible' || frame != null) return
      frame = requestAnimationFrame(() => {
        frame = null
        noteIncomingScreenShown(callId, 'in-app-overlay')
      })
    }
    markWhenVisible()
    document.addEventListener('visibilitychange', markWhenVisible)
    return () => {
      document.removeEventListener('visibilitychange', markWhenVisible)
      if (frame != null) cancelAnimationFrame(frame)
    }
  }, [callId, ringingInApp])
}
